import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { blockExportMetadata, blockSubjectHasProxyState, customizedBlock, viewShowVenncast } from 'venn-state';
import {
  convertMultipleSeriesDataToExcel,
  getAnalyzedSubjectFromRequestSubject,
  getCumulativeVenncastExportData,
  useHasFF,
} from 'venn-utils';

import { DownloadableContentBlock } from '../../../content-block';
import { EmptyHeaderSpace } from '../../../content-block/DownloadableContentBlock';
import { useAppPrintMode } from '../../../print';
import { BarChart, WATERMARK_POSITION_TOP } from '../../../studio-blocks';
import StyledEmptyState from '../../../studio-blocks/components/StyledEmptyState';
import LineChart from '../../../studio-blocks/components/charts/LineChart';
import { getThirdPartyExportMessageRow } from '../../../studio-blocks/logic/exportUtils';
import useExportUpdate from '../../../studio-blocks/logic/useExportUpdate';
import { useSelectedMetrics } from '../../../studio-blocks/logic/useSelectedMetrics';
import { useSubjectColors } from '../../../studio-blocks/logic/useSubjectColors';
import { useBlockContext } from '../../contexts/block-context';
import { useFilteredRequests } from '../../utils/hooks/use-filtered-requests';
import { useTimeseries } from '../../utils/hooks/use-time-series';
import { usePageProxySymbol } from '../../components/core/block-subject-footnotes-and-superscripts';

interface TimeSeriesContent {
  id: string;
}

const PERCENTAGE_DRAWDOWN_KEY = 'historicalDrawdown';

export const TimeSeriesContent = ({ id }: TimeSeriesContent) => {
  const hasStudioVenncastFF = useHasFF('studio_venncast_ff');

  const { inPrintModeOrReportLab } = useAppPrintMode();
  const { blockRef: downloadableContentRef } = useBlockContext();
  const { requests, analyses } = useFilteredRequests(id);
  const analyzedSubjects = useMemo(
    () => requests.map(({ subject }) => getAnalyzedSubjectFromRequestSubject(subject)),
    [requests],
  );
  const subjectColors = useSubjectColors(analyzedSubjects);
  const metrics = useSelectedMetrics();

  const exportMetaData = useRecoilValue(blockExportMetadata(id));
  const { relativeToBenchmark, infoGraphicType, header } = useRecoilValue(customizedBlock(id));
  const showVennCast = useRecoilValue(viewShowVenncast(id));
  const subjectHasProxy = useRecoilValue(blockSubjectHasProxyState(id));
  const proxySymbol = usePageProxySymbol();

  const metric = metrics ? metrics[0] : undefined;
  const addVenncastSeries = hasStudioVenncastFF && showVennCast && requests.length === 1;

  const { series, exportableSeries } = useTimeseries({
    requests,
    analyses,
    metric,
    subjectColors,
    addVenncastSeries,
    subjectHasProxy,
    proxySymbol,
  });

  const defaultHeader =
    relativeToBenchmark && metric?.relativeLabel ? metric.relativeLabel : !metric ? 'Builder Chart' : metric.label;

  const showThirdPartyDataExportMessage = series.length && !exportableSeries.length;

  const excelDataFn = useMemo(() => {
    if (showThirdPartyDataExportMessage) {
      return () => [getThirdPartyExportMessageRow()];
    }

    if (addVenncastSeries && exportableSeries.length) {
      const venncast = exportableSeries[0]!.venncast;
      if (venncast) {
        return () => getCumulativeVenncastExportData(exportableSeries[0]!.name, venncast, true);
      }
      return undefined;
    }

    return () =>
      convertMultipleSeriesDataToExcel(
        exportableSeries.map((s) => ({ name: s.name, series: s.data })),
        metric?.dataType !== 'NUMERIC',
      );
  }, [showThirdPartyDataExportMessage, exportableSeries, addVenncastSeries, metric?.dataType]);

  useExportUpdate({ exportMetaData, excelDataFn, selectedRefId: id });

  if (isEmpty(series)) {
    return <StyledEmptyState selectedRefId={id} header="Unable to run analysis" message="Chart is empty" />;
  }

  const Chart =
    infoGraphicType === 'LINE' ? (
      <LineChart
        start={requests?.[0]?.dateRange.from}
        end={requests?.[0]?.dateRange.to}
        series={series}
        frequency={requests?.[0]?.frequency ?? 'MONTHLY'}
        yAxisUnitFormat={metric?.dataType === 'NUMERIC' ? 'ratio' : 'percent'}
        inPrintMode={inPrintModeOrReportLab}
        hasDrawdownLines={metric?.key === PERCENTAGE_DRAWDOWN_KEY}
        venncastEnabled={hasStudioVenncastFF && showVennCast}
        subjectType={requests?.[0]!.subject?.subjectType === 'PORTFOLIO' ? 'portfolio' : 'investment'}
      />
    ) : infoGraphicType === 'BAR' ? (
      <BarChart
        start={requests?.[0]?.dateRange.from}
        end={requests?.[0]?.dateRange.to}
        series={series}
        frequency={requests?.[0]?.frequency ?? 'MONTHLY'}
        yAxisUnitFormat={metric?.dataType === 'NUMERIC' ? 'ratio' : 'percent'}
      />
    ) : null;

  if (inPrintModeOrReportLab) {
    return Chart;
  }

  return (
    <DownloadableContentBlock
      header={<EmptyHeaderSpace />}
      noBorder
      downloadable={{
        png: true,
        options: {
          fileName: header ?? defaultHeader,
          watermark: { top: WATERMARK_POSITION_TOP, right: 20 },
        },
        tracking: {
          description: 'BUILDER',
          relativeToBenchmark,
          userUploaded: false,
          subjectType: undefined,
          subjectId: undefined,
        },
        disabled: series.length === 0,
        target: downloadableContentRef,
      }}
    >
      {Chart}
    </DownloadableContentBlock>
  );
};
